import { inject, Injectable } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom, Observable } from 'rxjs';
import {
  EmailMergeDto,
  RepairTaskContractorOptionDto,
  RepairTaskDetailDto,
  RepairTaskService,
} from '@artemis-software/wr-api';
import {
  RepairTaskCreateDialogComponent,
  RepairTaskCreateDialogResult,
} from '@components/dialog/repair-task-create-dialog/repair-task-create-dialog.component';
import { SendEmailComponent } from '@components/dialog/send-email/send-email.component';

export interface RepairTaskCreateContext {
  buildingId: string;
  inspectionEntryId?: string;
  buildingEquipmentId?: string;
  title: string;
  description: string;
  contractorFilter?: (option: RepairTaskContractorOptionDto) => boolean;
  loadContractorOptions?: () => Observable<RepairTaskContractorOptionDto[]>;
  emptyContractorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class RepairTaskDispatchService {
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  async createAndSend(context: RepairTaskCreateContext): Promise<RepairTaskDetailDto | undefined> {
    const selection: RepairTaskCreateDialogResult | undefined = await firstValueFrom(
      this.dialogService.open(RepairTaskCreateDialogComponent, {
        closeOnBackdropClick: false,
        context: {
          buildingId: context.buildingId,
          initialTitle: context.title,
          initialDescription: context.description,
          contractorFilter: context.contractorFilter ?? (() => true),
          ...(context.loadContractorOptions ? { contractorOptionsLoader: context.loadContractorOptions } : {}),
          ...(context.emptyContractorMessage ? { emptyContractorMessage: context.emptyContractorMessage } : {}),
        },
      }).onClose,
    );
    if (!selection) {
      return undefined;
    }
    const created = await firstValueFrom(this.repairTaskService.merge({
      id: undefined,
      title: selection.title,
      description: selection.description,
      status: undefined,
      deadline: undefined,
      inspectionEntryId: context.inspectionEntryId,
      buildingEquipmentId: context.buildingEquipmentId,
      buildingId: context.buildingId,
      contractorId: selection.contractor.id,
    }));
    const sent = await this.sendMail(created.id);
    if (!sent) {
      this.toastrService.info('Der Reparaturauftrag wurde als Entwurf gespeichert', 'Nicht versendet');
      return created;
    }
    return sent;
  }

  async sendMail(repairTaskId: string): Promise<RepairTaskDetailDto | undefined> {
    const draft = await firstValueFrom(this.repairTaskService.generateMail(repairTaskId));
    const email: EmailMergeDto | undefined = await firstValueFrom(
      this.dialogService.open(SendEmailComponent, {
        closeOnBackdropClick: false,
        context: {
          title: 'Reparaturauftrag an Dienstleister senden',
          recipients: draft.recipients,
          emailMessage: { subject: draft.subject, htmlBody: draft.htmlBody, attachments: [] },
          message: draft.photoCount
            ? `${draft.photoCount} Foto(s) aus der Prüfpunktzeile werden angehängt.`
            : '',
          allowAttachments: false,
        },
      }).onClose,
    );
    if (!email) {
      return undefined;
    }
    const sent = await firstValueFrom(this.repairTaskService.send(repairTaskId, {
      subject: email.subject ?? '',
      htmlBody: email.htmlBody ?? '',
      recipients: email.recipients ?? [],
      cc: email.cc,
      sendCopyToSender: email.sendCopyToSender,
    }));
    this.toastrService.success('Der Reparaturauftrag wurde an den Dienstleister gesendet', 'Versendet');
    return sent;
  }
}
